import React, { useEffect, useCallback, useRef } from 'react';
import { Platform, ViewProps, ViewStyle } from 'react-native';
import { DeviceManager } from '@/webrtc/device-manager/device.manager';

const RTCView = Platform.OS !== 'web' && require('react-native-webrtc').RTCView;

interface Props extends ViewProps {
  objectFit?: 'cover' | 'contain';
  src?: any;
  style?: ViewStyle;
  muted?: boolean;
  zIndex?: number;
  deviceManager: DeviceManager;
}

function BaseRTCViewWeb({ src, objectFit, style, muted, deviceManager, zIndex }: Props) {
  // We keep a standard ref just so our srcObject effect can access the node
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // 1. The Callback Ref: React calls this the exact millisecond the DOM node mounts or unmounts
  const handleVideoMount = useCallback(
    (node: HTMLVideoElement | null) => {
      // If we had an old node, unregister it
      if (videoRef.current) {
        deviceManager.onVideoElementUnloaded(videoRef.current);
      }

      // If a new node just mounted, register it
      if (node) {
        deviceManager.onVideoElementLoaded(node);
      }

      // Save the reference so the useEffect below can use it
      videoRef.current = node;
    },
    [deviceManager],
  );

  // 2. The Media Stream Effect: Only runs when the `src` prop changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    if ('srcObject' in video) {
      if (video.srcObject !== src) {
        video.srcObject = src;
      }
    } else {
      // Fallback for older browsers
      (video as any).src = window.URL.createObjectURL(src);
    }
  }, [src]); // Only depend on 'src'

  return (
    <video
      id="rtc-video"
      ref={handleVideoMount} // Pass the callback here!
      autoPlay
      playsInline
      muted={muted}
      style={{ objectFit, zIndex, ...style } as any}
    />
  );
}

function BaseRTCViewMobile({ src, objectFit = 'cover', style, zIndex }: Props) {
  return <RTCView zOrder={zIndex} objectFit={objectFit} streamURL={src?.toURL()} style={style} />;
}

export const AppRTCView = React.memo(Platform.OS === 'web' ? BaseRTCViewWeb : BaseRTCViewMobile);
